import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, InputNumber, Modal, Popconfirm, Space, Spin, Table, Tag, Tooltip } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";

import salesRevenueApi from "../../../api/salesRevenueApi";
import { getManualAdjustedConflict, renderManualAdjustedList } from "./salesRevenueHelpers";

const SOURCE_TEXT = {
    AUTO_SEED: "本单自有",
    MANUAL_LINK: "手动关联",
};

// 后端错误码 → 中文提示
const CODE_TEXT = {
    SALES_BOOKING_LEG_FUEL_BURN_MISSING: "有航段未填油耗，无法生成",
    SALES_AIRPORT_FUEL_PRICE_MISSING: "有机场当月市场油价缺失，请先去补价",
    SALES_BOOKING_LEG_EMPTY: "该订单未挑任何航段",
    SALES_CONTRACT_BASE_FUEL_PRICE_REQUIRED: "合同未设置基准油价",
    SALES_BOOKING_LEG_ALREADY_LINKED: "该航段已在其他订单的燃油结算里",
    SALES_FLIGHT_OPERATION_NOT_FOUND: "找不到该航段",
    SALES_BOOKING_LEG_NOT_FOUND: "找不到该挑段记录",
    SALES_RECEIVABLE_LOCKED: "已开票 / 收款的应收不可重算",
    SALES_BOOKING_LEG_FUEL_BURN_INVALID: "油耗必须大于 0",
    SALES_CONTRACT_KIND_MISMATCH: "该订单不是按段油耗合同",
    SALES_RECEIVABLE_MANUALLY_ADJUSTED: "该应收存在手工调整，需确认强制重算",
};

function extractError(error, fallback = "操作失败") {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") {
        return CODE_TEXT[detail] || detail || fallback;
    }
    if (detail && typeof detail === "object") {
        const code = detail.detail;
        let message = CODE_TEXT[code] || code || fallback;
        if (Array.isArray(detail.missing) && detail.missing.length) {
            message += `：${detail.missing.map((item) => `${item.airport} ${item.month}`).join("、")}`;
        }
        if (Array.isArray(detail.legs) && detail.legs.length) {
            message += `：${detail.legs.map((item) => item.route_display || item.leg_id).join("、")}`;
        }
        if (detail.linked_booking_no) {
            message += `（已归属 ${detail.linked_booking_no}）`;
        }
        return message;
    }
    return error?.message || fallback;
}

function toNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
}

function formatAmount(value) {
    const num = toNumber(value);
    if (num === null) return "—";
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * PER_LEG_BURN「航段油耗」面板：挑段 + 填油耗 + 生成整单燃油应收。
 * 自包含：自行加载 leg-fuel-summary，管理油耗草稿、添加/移除段、保存、生成。
 */
export default function LegFuelBurnPanel({ bookingId, canWrite, canGenerate, messageApi, modalApi, onGenerated }) {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [burnDraft, setBurnDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [linking, setLinking] = useState(false);
    const [addForm] = Form.useForm();

    const load = useCallback(async () => {
        if (!bookingId) return;
        setLoading(true);
        try {
            const response = await salesRevenueApi.getBookingLegFuelSummary(bookingId);
            const data = response.data || {};
            setSummary(data);
            const draft = {};
            (data.legs || []).forEach((leg) => {
                draft[leg.leg_id] = toNumber(leg.fuel_burn_kg);
            });
            setBurnDraft(draft);
        } catch (error) {
            messageApi.error(extractError(error, "获取航段油耗失败"));
        } finally {
            setLoading(false);
        }
    }, [bookingId, messageApi]);

    useEffect(() => {
        load();
    }, [load]);

    const base = toNumber(summary?.base_fuel_price_per_mt);
    const currency = summary?.currency || "USD";

    const liveLegAmount = useCallback(
        (leg) => {
            const price = toNumber(leg.month_avg_price);
            const burn = burnDraft[leg.leg_id];
            if (price === null || base === null || burn === null || burn === undefined) return null;
            return ((price - base) * burn) / 1000;
        },
        [base, burnDraft],
    );

    const liveTotals = useMemo(() => {
        const legs = summary?.legs || [];
        let total = 0;
        let missingBurn = 0;
        let missingPrice = 0;
        legs.forEach((leg) => {
            const burn = burnDraft[leg.leg_id];
            if (burn === null || burn === undefined) missingBurn += 1;
            if (leg.price_missing) missingPrice += 1;
            const amount = liveLegAmount(leg);
            if (amount !== null) total += amount;
        });
        const ready = legs.length > 0 && missingBurn === 0 && missingPrice === 0 && base !== null;
        return { total, missingBurn, missingPrice, ready, legCount: legs.length };
    }, [summary, burnDraft, liveLegAmount, base]);

    const handleBurnChange = (legId, value) => {
        setBurnDraft((prev) => ({ ...prev, [legId]: value === null || value === undefined ? null : Number(value) }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const items = (summary?.legs || []).map((leg) => ({
                leg_id: leg.leg_id,
                fuel_burn_kg: burnDraft[leg.leg_id] ?? null,
            }));
            await salesRevenueApi.saveBookingLegFuelBurns(bookingId, items);
            messageApi.success("挑段与油耗已保存");
            await load();
        } catch (error) {
            messageApi.error(extractError(error, "保存失败"));
        } finally {
            setSaving(false);
        }
    };

    const runGenerate = useCallback(
        async (force) => {
            setGenerating(true);
            try {
                const response = await salesRevenueApi.recalculateFuelSurcharge(bookingId, { force });
                const action = response.data?.data?.action;
                if (action === "SKIPPED") {
                    messageApi.info(response.data?.message || "这一版不算燃油");
                    return;
                }
                if (action === "SKIPPED_EXISTS" && !force) {
                    setGenerating(false);
                    modalApi.confirm({
                        title: "该订单已存在燃油应收",
                        content: "是否按当前挑段与油耗重新计算并覆盖原应收？",
                        okText: "重新生成",
                        cancelText: "取消",
                        onOk: () => runGenerate(true),
                    });
                    return;
                }
                messageApi.success(action === "UPDATED" ? "燃油应收已重算" : "燃油应收已生成");
                await load();
                if (onGenerated) await onGenerated();
            } catch (error) {
                const conflict = getManualAdjustedConflict(error);
                if (!force && conflict) {
                    modalApi.confirm({
                        title: "存在手工调整，确认强制重算？",
                        content: renderManualAdjustedList(conflict, "重新生成"),
                        okText: "强制重算（清空手工值）",
                        okButtonProps: { danger: true },
                        cancelText: "保留手工值（取消）",
                        onOk: () => runGenerate(true),
                    });
                    return;
                }
                messageApi.error(extractError(error, "生成燃油应收失败"));
            } finally {
                setGenerating(false);
            }
        },
        [bookingId, load, messageApi, modalApi, onGenerated],
    );

    const handleGenerate = () => {
        if (!liveTotals.ready) {
            modalApi.confirm({
                title: "数据尚未齐全",
                content: "当前存在缺油耗或缺市场价的航段，仍要尝试生成吗？（后端会拒绝不齐的单）",
                okText: "仍要尝试",
                cancelText: "取消",
                onOk: () => runGenerate(false),
            });
            return;
        }
        runGenerate(false);
    };

    const handleUnlink = async (legId) => {
        try {
            await salesRevenueApi.unlinkBookingLegForFuel(bookingId, legId);
            messageApi.success("航段已移出燃油结算");
            await load();
        } catch (error) {
            messageApi.error(extractError(error, "移除失败"));
        }
    };

    const handleLink = async () => {
        try {
            const values = await addForm.validateFields();
            setLinking(true);
            await salesRevenueApi.linkBookingLegForFuel(bookingId, values.flight_operation_id);
            messageApi.success("航段已加入燃油结算");
            setAddOpen(false);
            addForm.resetFields();
            await load();
        } catch (error) {
            if (error?.errorFields) return;
            messageApi.error(extractError(error, "添加航段失败"));
        } finally {
            setLinking(false);
        }
    };

    const columns = [
        {
            title: "航段",
            dataIndex: "route_display",
            render: (value, record) => value || `${record.departure_airport || "—"} → ${record.arrival_airport || "—"}`,
        },
        { title: "航班号", dataIndex: "flight_number", width: 90, render: (value) => value || "—" },
        { title: "起飞日", dataIndex: "flight_date", width: 110, render: (value) => value || "—" },
        { title: "机型", dataIndex: "aircraft_type", width: 80, render: (value) => value || "—" },
        {
            title: "来源",
            dataIndex: "source",
            width: 90,
            render: (value) => <Tag color={value === "MANUAL_LINK" ? "purple" : "blue"}>{SOURCE_TEXT[value] || value}</Tag>,
        },
        {
            title: "油耗(kg)",
            dataIndex: "fuel_burn_kg",
            width: 130,
            render: (_, record) => (
                <InputNumber
                    min={0}
                    precision={2}
                    style={{ width: "100%" }}
                    disabled={!canWrite}
                    value={burnDraft[record.leg_id]}
                    onChange={(value) => handleBurnChange(record.leg_id, value)}
                    placeholder="待填"
                />
            ),
        },
        {
            title: "月均价",
            dataIndex: "month_avg_price",
            width: 130,
            render: (value, record) => {
                if (record.price_missing) {
                    return <Tag color="red">缺价</Tag>;
                }
                const label = `${formatAmount(value)} ${currency}`;
                const tip = (
                    <div>
                        <div>{`${record.price_month || ""} 月均价`}</div>
                        <div>{`用 ${record.weeks_used}/${record.weeks_expected} 周`}</div>
                        {record.week_labels?.length ? <div>{record.week_labels.join("、")}</div> : null}
                    </div>
                );
                return (
                    <Tooltip title={tip}>
                        <span>
                            {label}
                            {record.is_price_complete ? null : (
                                <Tag color="orange" style={{ marginLeft: 4 }}>
                                    按{record.weeks_used}周
                                </Tag>
                            )}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "本段燃油",
            key: "leg_fuel_amount",
            width: 120,
            render: (_, record) => {
                const amount = liveLegAmount(record);
                return <span>{amount === null ? "—" : `${formatAmount(amount)} ${currency}`}</span>;
            },
        },
        {
            title: "操作",
            key: "action",
            width: 80,
            render: (_, record) =>
                canWrite ? (
                    <Popconfirm title="移出该航段？" okText="移除" cancelText="取消" onConfirm={() => handleUnlink(record.leg_id)}>
                        <Button type="link" danger size="small">
                            移除
                        </Button>
                    </Popconfirm>
                ) : null,
        },
    ];

    return (
        <div className="sales-revenue-card compact">
            <div className="sales-revenue-card-header">
                <span>航段油耗（按段燃油结算）</span>
                <Space>
                    {canWrite ? (
                        <Button size="small" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                            添加航段
                        </Button>
                    ) : null}
                    <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        刷新
                    </Button>
                </Space>
            </div>
            <div className="sales-revenue-card-body">
                <Spin spinning={loading}>
                    {summary?.has_existing_receivable ? (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message={`已生成燃油应收 ${summary.existing_receivable_no || ""}，重新生成会覆盖原应收。`}
                        />
                    ) : null}
                    {liveTotals.missingPrice > 0 ? (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message={`有 ${liveTotals.missingPrice} 段缺当月市场油价，需先到结算端补价：${(summary?.missing_price_airports || []).join("、")}`}
                        />
                    ) : null}
                    {summary?.has_partial_weeks ? (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 8 }}
                            message="部分机场当月市场价缺周次，已按现有周次平均，请知悉。"
                        />
                    ) : null}
                    <Table
                        rowKey="leg_id"
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={summary?.legs || []}
                        scroll={{ x: 960 }}
                        locale={{ emptyText: "尚未挑段：点「添加航段」把参与结算的航段加进来" }}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                        <div>
                            <span style={{ marginRight: 16 }}>
                                基准油价：<strong>{base === null ? "未设置" : `${formatAmount(base)} ${currency}/MT`}</strong>
                            </span>
                            <span>
                                本单燃油合计：
                                <strong style={{ color: liveTotals.ready ? "var(--sales-primary, #1677ff)" : "#999" }}>
                                    {liveTotals.legCount === 0 ? "—" : `${formatAmount(liveTotals.total)} ${currency}`}
                                </strong>
                                {liveTotals.missingBurn > 0 ? (
                                    <span style={{ color: "#faad14", marginLeft: 8 }}>（{liveTotals.missingBurn} 段待填油耗）</span>
                                ) : null}
                            </span>
                        </div>
                        <Space>
                            {canWrite ? (
                                <Button loading={saving} onClick={handleSave}>
                                    保存挑段与油耗
                                </Button>
                            ) : null}
                            {canGenerate ? (
                                <Button type="primary" loading={generating} onClick={handleGenerate}>
                                    生成燃油应收
                                </Button>
                            ) : null}
                        </Space>
                    </div>
                </Spin>
            </div>

            <Modal
                open={addOpen}
                title="添加航段到燃油结算"
                onCancel={() => setAddOpen(false)}
                onOk={handleLink}
                confirmLoading={linking}
                okText="添加"
                cancelText="取消"
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="输入要并入本单燃油结算的航段 FlightOperation ID（一般是同一圈的 CR 段 / 调机段）。一段全局只能归属一张订单的燃油结算。"
                />
                <Form form={addForm} layout="vertical">
                    <Form.Item
                        label="航段 ID (flight_operation_id)"
                        name="flight_operation_id"
                        rules={[{ required: true, message: "请输入航段 ID" }]}
                    >
                        <InputNumber min={1} precision={0} style={{ width: "100%" }} placeholder="如 10231" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
